import { Message } from "@arco-design/web-react";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { resolveReadableHttpErrorMessage } from "../../../lib/http/errors";
import { dicMajorDefaultListParams } from "../config";
import {
  createDicMajor,
  createDicMajorAlias,
  deleteDicMajorAlias,
  fetchDicMajorList,
  updateDicMajor,
  updateDicMajorAlias,
} from "../repo/dicMajorRepo";
import {
  normalizeDicMajorFilters,
  resolvePageAfterDelete,
} from "../service/dicMajorService";
import type {
  DicMajorAliasItem,
  DicMajorAliasMutation,
  DicMajorItem,
  DicMajorListFilters,
  DicMajorMutation,
} from "../types";

const dicMajorQueryRootKey = ["dic_major", "list"] as const;

/** 管理标准专业列表页查询、分页和变更行为。 */
export function useDicMajorRuntime() {
  const queryClient = useQueryClient();
  const [draftFilters, setDraftFilters] = useState<DicMajorListFilters>({});
  const [submittedFilters, setSubmittedFilters] = useState<DicMajorListFilters>({});
  const [page, setPage] = useState(dicMajorDefaultListParams.page);
  const [pageSize, setPageSize] = useState(dicMajorDefaultListParams.pageSize);
  const [mutationErrorMessage, setMutationErrorMessage] = useState("");

  const listParams = useMemo(
    () => ({
      ...dicMajorDefaultListParams,
      ...submittedFilters,
      page,
      pageSize,
    }),
    [page, pageSize, submittedFilters],
  );

  const query = useQuery({
    queryKey: [...dicMajorQueryRootKey, listParams],
    queryFn: () => fetchDicMajorList(listParams),
  });

  const listPage = query.data ?? {
    items: [],
    page,
    pageSize,
    total: 0,
  };

  const invalidateList = () =>
    queryClient.invalidateQueries({
      queryKey: dicMajorQueryRootKey,
    });

  const createMajorMutation = useMutation({
    mutationFn: createDicMajor,
    onSuccess: (item) => {
      if (item.vectorSyncWarning) Message.warning({ content: item.vectorSyncWarning, duration: 8000 });
      return invalidateList();
    },
  });
  const updateMajorMutation = useMutation({
    mutationFn: ({ majorId, payload }: { majorId: string; payload: DicMajorMutation }) =>
      updateDicMajor(majorId, payload),
    onSuccess: (item) => {
      if (item.vectorSyncWarning) Message.warning({ content: item.vectorSyncWarning, duration: 8000 });
      return invalidateList();
    },
  });
  const createAliasMutation = useMutation({
    mutationFn: ({ majorId, payload }: { majorId: string; payload: DicMajorAliasMutation }) =>
      createDicMajorAlias(majorId, payload),
    onSuccess: invalidateList,
  });
  const updateAliasMutation = useMutation({
    mutationFn: ({
      majorId,
      majorAliasId,
      payload,
    }: {
      majorId: string;
      majorAliasId: string;
      payload: DicMajorAliasMutation;
    }) => updateDicMajorAlias(majorId, majorAliasId, payload),
    onSuccess: invalidateList,
  });
  const deleteAliasMutation = useMutation({
    mutationFn: ({ majorId, majorAliasId }: { majorId: string; majorAliasId: string }) =>
      deleteDicMajorAlias(majorId, majorAliasId),
  });

  return {
    draftFilters,
    items: listPage.items,
    loading: query.isLoading,
    page,
    pageSize,
    total: listPage.total,
    errorMessage: query.error ? "标准专业列表加载失败，请稍后重试。" : "",
    mutationErrorMessage,
    setDraftFilters,
    clearMutationError() {
      setMutationErrorMessage("");
    },
    handleSearch() {
      setSubmittedFilters(normalizeDicMajorFilters(draftFilters));
      setPage(1);
    },
    handleReset() {
      setDraftFilters({});
      setSubmittedFilters({});
      setPage(1);
    },
    handlePageChange(nextPage: number) {
      setPage(Math.max(nextPage, 1));
    },
    handlePageSizeChange(nextPageSize: number) {
      setPageSize(nextPageSize);
      setPage(1);
    },
    async handleCreateMajor(payload: DicMajorMutation) {
      await runMutation(() => createMajorMutation.mutateAsync(payload), setMutationErrorMessage);
    },
    async handleUpdateMajor(major: DicMajorItem, payload: DicMajorMutation) {
      await runMutation(
        () => updateMajorMutation.mutateAsync({ majorId: major.majorId, payload }),
        setMutationErrorMessage,
      );
    },
    async handleCreateAlias(major: DicMajorItem, payload: DicMajorAliasMutation) {
      await runMutation(
        () => createAliasMutation.mutateAsync({ majorId: major.majorId, payload }),
        setMutationErrorMessage,
      );
    },
    async handleUpdateAlias(
      major: DicMajorItem,
      alias: DicMajorAliasItem,
      payload: DicMajorAliasMutation,
    ) {
      await runMutation(
        () =>
          updateAliasMutation.mutateAsync({
            majorId: major.majorId,
            majorAliasId: alias.majorAliasId,
            payload,
          }),
        setMutationErrorMessage,
      );
    },
    async handleDeleteAlias(major: DicMajorItem, alias: DicMajorAliasItem) {
      await deleteAliasMutation.mutateAsync({
        majorId: major.majorId,
        majorAliasId: alias.majorAliasId,
      });
      const targetPage = resolvePageAfterDelete({
        currentPage: page,
        currentItemCount: listPage.items.length,
        pageSize,
        total: listPage.total,
      });
      setPage(targetPage);
      await queryClient.fetchQuery({
        queryKey: [
          ...dicMajorQueryRootKey,
          {
            ...dicMajorDefaultListParams,
            ...submittedFilters,
            page: targetPage,
            pageSize,
          },
        ],
        queryFn: () =>
          fetchDicMajorList({
            ...dicMajorDefaultListParams,
            ...submittedFilters,
            page: targetPage,
            pageSize,
          }),
      });
    },
  };
}

/** 执行变更请求并统一记录保存失败提示。 */
async function runMutation<T>(
  execute: () => Promise<T>,
  setMutationErrorMessage: (message: string) => void,
) {
  setMutationErrorMessage("");
  try {
    return await execute();
  } catch (error) {
    setMutationErrorMessage(
      resolveReadableHttpErrorMessage(error, "保存失败，请检查填写内容后重试。", {
        conflictCode: "DIC_MAJOR_NAME_CONFLICT",
        conflictFallback: "专业名称已存在，请勿重复新增。",
        opaqueConflictDetails: ["dic-major-name-conflict"],
        validationFallback: "请填写标准专业名称、专业大类；拼音码可留空由系统生成。",
      }),
    );
    throw error;
  }
}
